import { createChatClient, type CustomerAuth } from './client';
import { resolveApiBaseUrl } from './client/apiBaseUrl';
import { createWidget, type Widget, type WidgetTheme } from './widget';
import { getWidgetLocale, initWidgetLocale, type WidgetLocale } from './widget/i18n';
import type { CustomerInfoProvider } from './widget/customerInfo';
import {
  bridgeFor,
  consumeSignInStarted,
  establishForHost,
  isSameSite,
  markSignInStarted,
  resolveAccountUrl,
  type FlixIdentityBridge,
  type HostAuthState,
} from './widget/flixIdentityAuth';
import type { CreateWidgetOptions } from './widget/widget';

export interface FlixChatConfig {
  // Absent for a logged-out visitor — the widget still renders in that case
  // (see CreateWidgetOptions in ./widget), it just shows a login/register
  // prompt instead of a conversation. Creating conversations remains limited
  // to logged-in customers; there is no guest identity.
  customerId?: string;
  apiBaseUrl?: string;
  webSocketBaseUrl?: string;
  loginUrl?: string;
  registerUrl?: string;
  // The Flix Identity origin for this storefront (data-identity-url). When
  // absent the widget uses the one the storefront itself publishes
  // (window.flixIdentityConfig.apiBaseUrl), and when there is none at all
  // the Flix Identity bridge is simply not used. An empty string switches
  // the bridge off explicitly.
  identityBaseUrl?: string;
  // The storefront's account page (data-account-url), offered to a visitor
  // whose Flix account is not yet linked to a store customer profile.
  // Defaults to /pages/account under the page's Shopify root; must be on
  // this page's own origin.
  accountUrl?: string;
  // Optional override (data-locale="ar"/"en"). When absent, the widget
  // follows the page's own <html lang> — the same signal Shopify sets per
  // storefront locale — so a bilingual store gets the matching language
  // with zero configuration.
  locale?: WidgetLocale;
  theme?: WidgetTheme;
  // Extension point, not a live feature — see CreateWidgetOptions in
  // ./widget. Only settable programmatically (window.FlixChatConfig or
  // FlixChat.init()), since a data-* attribute can't carry a function.
  // Undefined by default: no data source is wired up anywhere in this repo.
  getCustomerInfo?: CustomerInfoProvider;
}

export interface FlixChatBootstrap {
  init(config?: FlixChatConfig): void;
  destroy(): void;
}

// What the storefront already exposes about its own Flix Identity session.
// Declared structurally, and every member optional: the widget works
// without any of it, and reads only this much when it is there.
interface HostAuthClient {
  getState?: () => unknown;
  checkSession?: () => Promise<unknown>;
}

declare global {
  interface Window {
    FlixChatConfig?: FlixChatConfig;
    FlixChat?: FlixChatBootstrap;
    __flixChatWidgetInitialized?: boolean;
    FlixAuthClient?: HostAuthClient;
    flixIdentityConfig?: { apiBaseUrl?: unknown };
    Shopify?: { routes?: { root?: unknown } };
  }
}

function readPosition(value: string | undefined): WidgetTheme['position'] {
  return value === 'bottom-left' ? 'bottom-left' : undefined;
}

function readLocale(value: string | undefined): WidgetLocale | undefined {
  return value === 'ar' || value === 'en' ? value : undefined;
}

// Reads config off the <script> tag itself (data-customer-id, etc.) so a
// theme can configure the widget with no separate JS at all. A global
// window.FlixChatConfig, if present, is merged on top and wins on conflict,
// for themes that want more structured/programmatic control.
//
// data-theme-primary-color is deliberately no longer read: the widget's
// colors are a fixed part of the design (see design-system/README.md), and
// honoring an embed-supplied accent let a stray theme-editor setting break
// them in production — exactly what happened with the App Embed's color
// field. Only position (a layout choice, not a color) remains themeable.
function readScriptConfig(): FlixChatConfig {
  // document.currentScript is null when the bundle is loaded with `defer`
  // (e.g. a Shopify theme app embed), because deferred scripts execute after
  // parsing completes. Fall back to locating our own <script> tag by src so
  // the data-* attributes are still read in that case.
  const script =
    document.currentScript instanceof HTMLScriptElement
      ? document.currentScript
      : document.querySelector<HTMLScriptElement>('script[src*="flix-chat-widget.js"]');

  if (!(script instanceof HTMLScriptElement)) {
    return {};
  }

  const { dataset } = script;
  const theme: WidgetTheme = {};

  const position = readPosition(dataset.themePosition);
  if (position) {
    theme.position = position;
  }

  return {
    customerId: dataset.customerId,
    apiBaseUrl: dataset.apiBaseUrl,
    webSocketBaseUrl: dataset.websocketBaseUrl,
    loginUrl: dataset.loginUrl,
    registerUrl: dataset.registerUrl,
    identityBaseUrl: dataset.identityUrl,
    accountUrl: dataset.accountUrl,
    locale: readLocale(dataset.locale),
    theme: Object.keys(theme).length > 0 ? theme : undefined,
  };
}

function resolveConfig(explicitConfig: FlixChatConfig | undefined): FlixChatConfig {
  const scriptConfig = readScriptConfig();
  const globalConfig = window.FlixChatConfig ?? {};

  // An unset data attribute must not blank out a value another source
  // provides, so undefined entries are dropped before merging.
  const defined = (config: FlixChatConfig): FlixChatConfig =>
    Object.fromEntries(Object.entries(config).filter(([, value]) => value !== undefined));

  return {
    ...defined(scriptConfig),
    ...defined(globalConfig),
    ...defined(explicitConfig ?? {}),
    theme: { ...scriptConfig.theme, ...globalConfig.theme, ...explicitConfig?.theme },
  };
}

// ---- Flix Identity: what the host page knows ----

const HOST_AUTH_EVENT = 'flix:auth:state-changed';
// The storefront probes its own session on every page load; this is how
// long the widget will wait for that answer before going ahead without it.
const HOST_AUTH_WAIT_MS = 3000;

// The storefront's account client already asks Flix Identity "is this
// browser signed in" on every page. Reading its answer costs nothing —
// which is what lets a visitor who is NOT signed in load this widget
// without a single extra request. 'none' means the page offers no such
// answer at all.
function readHostAuthState(): HostAuthState | 'unknown' {
  const client = window.FlixAuthClient;
  if (!client || typeof client.getState !== 'function') {
    return 'none';
  }

  const state = client.getState();
  return state === 'signed-in' || state === 'signed-out' ? state : 'unknown';
}

function waitForHostAuthState(): Promise<HostAuthState> {
  const current = readHostAuthState();
  if (current !== 'unknown') {
    return Promise.resolve(current);
  }

  return new Promise((resolve) => {
    const settle = (): void => {
      document.removeEventListener(HOST_AUTH_EVENT, onChange);
      window.clearTimeout(timer);
      const state = readHostAuthState();
      resolve(state === 'unknown' ? 'none' : state);
    };
    const onChange = (): void => {
      if (readHostAuthState() !== 'unknown') {
        settle();
      }
    };
    const timer = window.setTimeout(settle, HOST_AUTH_WAIT_MS);
    document.addEventListener(HOST_AUTH_EVENT, onChange);
  });
}

// Asks the storefront's own account client to confirm — and if need be
// silently renew — the Flix Identity session. Its access token lasts 15
// minutes and is renewed with a single-use refresh token; a second renewal
// racing the storefront's would be read as a replay and end the customer's
// whole session. So the widget never renews it itself: the one client that
// owns that refresh does, and the widget only asks it to.
async function hostSessionStillValid(): Promise<boolean> {
  const client = window.FlixAuthClient;
  if (!client || typeof client.checkSession !== 'function') {
    return false;
  }

  try {
    const profile = await client.checkSession();
    return profile !== null && profile !== undefined;
  } catch {
    return false;
  }
}

// Requests to this origin carry the customer's Flix Identity cookie, and a
// visitor is sent to it to sign in — so it has to be a real https origin,
// not whatever string happened to be configured.
function resolveIdentityBaseUrl(config: FlixChatConfig): string | null {
  const configured = config.identityBaseUrl ?? window.flixIdentityConfig?.apiBaseUrl;
  if (typeof configured !== 'string' || configured.trim().length === 0) {
    return null;
  }

  try {
    const url = new URL(configured);
    const isLoopback = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && isLoopback)) {
      return null;
    }
    // Only on the same site as this page. Elsewhere — a separate storefront
    // domain pointed at another brand's identity host — the session cookie
    // would never be sent, so the bridge is not used at all and the widget
    // keeps the sign-in it has always had.
    if (!isSameSite(window.location.hostname, url.hostname)) {
      return null;
    }
    return `${url.origin}${url.pathname}`.replace(/\/+$/, '');
  } catch {
    return null;
  }
}

// ---- Lifecycle ----

let activeWidget: Widget | null = null;
// Bumped by destroy(), so a bootstrap still waiting on the network from
// before it can tell it has been superseded and must not create a widget.
let generation = 0;
let lastExplicitConfig: FlixChatConfig | undefined;
// The bridge session this page is using, if it is using one.
let activeSession: { bridge: FlixIdentityBridge; token: string } | null = null;
// What the host said about its session when this widget was built — so a
// later change (signing in or out without leaving the page) is noticeable.
let hostStateAtBootstrap: HostAuthState | null = null;
let watchingHostAuthState = false;
// Set by reinitialize() when the panel was open, so the rebuilt widget
// opens again — otherwise pressing "Try again", or signing out with the
// panel in view, would make it vanish from under the visitor.
let reopenAfterBootstrap = false;

// Rebuilds the widget from scratch. Signing in or out changes what the
// widget is — a conversation or a prompt — and everything it holds, so it
// is rebuilt rather than patched.
function reinitialize(): void {
  const explicitConfig = lastExplicitConfig;
  const session = activeSession;
  const panel = document.getElementById('flix-chat-widget-panel');
  reopenAfterBootstrap = panel !== null && !panel.hidden;

  destroy();

  if (session && readHostAuthState() === 'signed-out') {
    // The customer signed out of Flix Identity on this page. The support
    // session would lapse within minutes on its own; this ends it now.
    void session.bridge.revoke(session.token);
  }

  init(explicitConfig);
}

function watchHostAuthState(): void {
  if (watchingHostAuthState) {
    return;
  }
  watchingHostAuthState = true;

  document.addEventListener(HOST_AUTH_EVENT, () => {
    const next = readHostAuthState();
    if (
      window.__flixChatWidgetInitialized &&
      hostStateAtBootstrap !== null &&
      (next === 'signed-in' || next === 'signed-out') &&
      next !== hostStateAtBootstrap
    ) {
      reinitialize();
    }
  });
}

interface CustomerIdentity {
  customerId?: string;
  customerAuth?: CustomerAuth;
  loginScreen?: CreateWidgetOptions['loginScreen'];
}

// Works out who the visitor is when the page did not say (no Shopify
// customer session). Resolves to nothing at all when the bridge is not in
// play, which leaves the widget exactly as it was before the bridge existed.
async function resolveFlixIdentity(
  bridge: FlixIdentityBridge,
  accountUrl: string | null,
  isCurrent: () => boolean,
): Promise<CustomerIdentity | null> {
  const hostState = await waitForHostAuthState();
  if (!isCurrent()) {
    return null;
  }
  hostStateAtBootstrap = hostState;
  watchHostAuthState();

  // No request for a visitor the host says is signed out; for anyone else
  // the support backend is asked first, and when it says the bridge is off
  // nothing is ever sent to Flix Identity (see establishForHost).
  const outcome = await establishForHost(bridge, hostState);

  if (!isCurrent()) {
    if (outcome.status === 'authenticated') {
      void bridge.revoke(outcome.token);
    }
    return null;
  }

  if (outcome.status === 'authenticated') {
    const customerId = outcome.customerId;
    activeSession = { bridge, token: outcome.token };
    let renewal: Promise<string | null> | null = null;

    return {
      customerId,
      customerAuth: {
        getToken: () => activeSession?.token ?? null,
        // Several requests can be refused at once when a session lapses;
        // they share one renewal instead of each starting their own.
        refresh(): Promise<string | null> {
          renewal ??= bridge
            .establish()
            .then(async (first) => {
              // "Signed out" on a page that has been open a while usually
              // means only that the short-lived access token lapsed. The
              // storefront's client is asked to renew it, and the handoff
              // is tried once more before the customer is treated as gone.
              const next =
                first.status === 'signed-out' && (await hostSessionStillValid())
                  ? await bridge.establish()
                  : first;

              if (!isCurrent()) {
                return null;
              }
              if (next.status === 'authenticated' && next.customerId === customerId) {
                activeSession = { bridge, token: next.token };
                return next.token;
              }
              // Signed out, or signed in as someone else: this widget was
              // built for a customer who is no longer here.
              activeSession = null;
              window.setTimeout(reinitialize, 0);
              return null;
            })
            .finally(() => {
              renewal = null;
            });
          return renewal;
        },
      },
    };
  }

  if (outcome.status === 'link-missing') {
    return {
      loginScreen: {
        linkMissing: { onRetry: reinitialize, accountUrl: accountUrl ?? undefined },
      },
    };
  }

  if (outcome.status === 'signed-out') {
    return {
      loginScreen: {
        // Offered only once the backend says the public may sign in this
        // way — otherwise the visitor would sign in and come back to this
        // same prompt. Off, or on for named accounts only, the links keep
        // their existing destination.
        resolveSignInUrl: async () =>
          (await bridge.availability()).signInOffered
            ? bridge.signInUrl(window.location.href, getWidgetLocale())
            : null,
        onSignInStart: markSignInStarted,
      },
    };
  }

  // 'unavailable'
  return {};
}

async function bootstrap(config: FlixChatConfig, startedAt: number): Promise<void> {
  const isCurrent = (): boolean => startedAt === generation;

  // An App Proxy path named on another of the shop's domains is used on
  // this page's own origin instead — see resolveApiBaseUrl.
  const apiBaseUrl = resolveApiBaseUrl(config.apiBaseUrl ?? '', window.location.origin);

  let identity: CustomerIdentity = { customerId: config.customerId };

  // A Shopify customer session, when the page has one, is used as it always
  // was and the bridge is never consulted — there is then no bridge at all
  // (see bridgeFor), and likewise on a page with no Flix Identity origin.
  const bridge = bridgeFor(
    config.customerId,
    config.customerId ? null : resolveIdentityBaseUrl(config),
    apiBaseUrl,
  );
  if (bridge !== null) {
    const accountUrl = resolveAccountUrl(
      config.accountUrl,
      window.Shopify?.routes?.root,
      window.location.origin,
    );
    const resolved = await resolveFlixIdentity(bridge, accountUrl, isCurrent);
    if (resolved === null) {
      return;
    }
    identity = resolved;
  }

  const client = createChatClient({
    httpBaseUrl: apiBaseUrl,
    webSocketBaseUrl: config.webSocketBaseUrl ?? '',
    customerAuth: identity.customerAuth,
  });

  activeWidget = createWidget({
    client,
    customerId: identity.customerId,
    loginUrl: config.loginUrl,
    registerUrl: config.registerUrl,
    loginScreen: identity.loginScreen,
    theme: config.theme,
    getCustomerInfo: config.getCustomerInfo,
  });

  activeWidget.initialize();

  // Back from signing in at Flix Identity, having left from the open
  // widget: put it back the way they left it. Only when the trip changed
  // something — a visitor who came back still signed out is not shown a
  // panel they did not ask for. (The note is consumed either way.)
  const returnedFromSignIn =
    identity.customerAuth !== undefined || identity.loginScreen?.linkMissing !== undefined;
  const cameBackSignedIn = consumeSignInStarted() && returnedFromSignIn;

  // …and a widget rebuilt in place while its panel was open stays open.
  const wasOpen = reopenAfterBootstrap;
  reopenAfterBootstrap = false;

  if (cameBackSignedIn || wasOpen) {
    activeWidget.open();
  }
}

function performInit(explicitConfig: FlixChatConfig | undefined): void {
  if (window.__flixChatWidgetInitialized) {
    return;
  }

  const config = resolveConfig(explicitConfig);

  // customerId is deliberately not required here — an absent customerId
  // means a logged-out visitor, which the widget itself handles (login/
  // register prompt instead of a conversation). apiBaseUrl/webSocketBaseUrl
  // are infrastructure config that must always be present regardless of
  // login state.
  if (!config.apiBaseUrl || !config.webSocketBaseUrl) {
    console.error(
      'Flix Chat widget: apiBaseUrl and webSocketBaseUrl are required (via data attributes or window.FlixChatConfig).',
    );
    return;
  }

  window.__flixChatWidgetInitialized = true;
  lastExplicitConfig = explicitConfig;

  // Must run before createWidget: every string and the RTL direction are
  // resolved while the DOM is being built.
  initWidgetLocale(config.locale);

  bootstrap(config, generation).catch((error: unknown) => {
    console.error('Flix Chat widget: failed to start.', error);
  });
}

// The public bootstrap API is safe to call at any point in the page
// lifecycle — including before <body> exists, e.g. from an inline script
// placed in <head>. If the DOM isn't ready yet, initialization is deferred
// rather than failing on a missing document.body.
function init(explicitConfig?: FlixChatConfig): void {
  if (document.body) {
    performInit(explicitConfig);
    return;
  }

  document.addEventListener('DOMContentLoaded', () => performInit(explicitConfig), { once: true });
}

function destroy(): void {
  generation += 1;
  activeWidget?.destroy();
  activeWidget = null;
  activeSession = null;
  hostStateAtBootstrap = null;
  window.__flixChatWidgetInitialized = false;
}

window.FlixChat = { init, destroy };

init();
